import { DeviceMode, type NodeV2, type NodeV2Style } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import React, { useCallback, useMemo, useState } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import type { IPositionArgs } from 'components/Editor/components/AbsolutePortal/types'
import { IconButton } from 'components/uiKit/Button'
import DropDown from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { addNode } from 'services/Store/Project/mutators/node'
import { getNodeStyle, mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'

import Picker from '../../Picker'

type AbsolutePortalPickersProps = Record<'prev' | 'next', Partial<IPositionArgs>>

const ROW: AbsolutePortalPickersProps = {
  prev: { placement: 'left', chain: 'off', translateX: '-50%', translateY: '-50%' },
  next: { placement: 'right', chain: 'off', translateX: '-50%', translateY: '-50%' },
}

const COLUMN: AbsolutePortalPickersProps = {
  prev: { placement: 'top', chain: 'off', translateY: '-50%', translateX: '-50%' },
  next: { placement: 'bottom', chain: 'off', translateY: '-50%', translateX: '-50%' },
}

const TYPE_MAP: Record<NodeV2Style['flexDirection'], AbsolutePortalPickersProps> = {
  row: ROW,
  column: COLUMN,
  'row-reverse': { ...ROW, prev: ROW.next, next: ROW.prev },
  'column-reverse': { ...COLUMN, prev: COLUMN.next, next: COLUMN.prev },
}

interface ILayoutGroupProps {
  mode: IBlockMode
  block: Block
  selected: NodeV2[]
  node: NodeV2
}

const FlexChildrenControls: React.FC<ILayoutGroupProps> = ({ block, mode, node, selected }) => {
  const dispatch = useProjectDispatch()
  const isPro = mode.editorMode === AppMode.pro
  const isDesktop = mode.deviceMode === DeviceMode.desktop
  const style = useMemo(() => {
    if (selected.length === 1) {
      return mergeNodeStyle(selected[0].style, mode.deviceMode)
    }
    return null
  }, [mode.deviceMode, selected])
  const isAbsolute = style?.flexFlow === 'absolute'

  const parentStyles = useProjectContext(getNodeStyle, block.uuid, node.id)

  const [showPrevPicker, setShowPrevPicker] = useState(false)
  const [showNextPicker, setShowNextPicker] = useState(false)
  const { prev, next } = TYPE_MAP[parentStyles?.flexDirection || 'column']

  const handleAddPrevChild = useCallback(
    (newNode: NodeV2, data?: { elements?: MapType<EditorElement> }) => {
      setShowPrevPicker(false)
      const index = Math.min(...selected.map((n) => node.children.indexOf(n.id)))
      const value = addNode(node.id, newNode, data, { index })(block)
      dispatch(updateBlock({ id: block?.uuid, value }))
    },
    [dispatch, block, node, selected],
  )

  const handleAddNextChild = useCallback(
    (newNode: NodeV2, data?: { elements?: MapType<EditorElement> }) => {
      setShowNextPicker(false)
      const index = Math.max(...selected.map((n) => node.children.indexOf(n.id))) + 1
      const value = addNode(node.id, newNode, data, { index })(block)
      dispatch(updateBlock({ id: block?.uuid, value }))
    },
    [dispatch, block, node, selected],
  )

  return (
    <>
      {isPro && isDesktop && !isAbsolute && (
        <AbsolutePortal {...prev} name='prev' recalculatingKey={block}>
          <DropDown
            onVisibleChange={setShowPrevPicker}
            overlay={<Picker block={block} mode={mode} node={node} onAdd={handleAddPrevChild} />}
            placement='right-start'
            styleType='clear'
            visible={showPrevPicker}
          >
            <IconButton
              background='var(--color-flex)'
              icon='add'
              name='addChildPrev'
              size={KitSize.XS}
              round
            />
          </DropDown>
        </AbsolutePortal>
      )}
      {isPro && isDesktop && !isAbsolute && (
        <AbsolutePortal {...next} name='next' recalculatingKey={block}>
          <DropDown
            onVisibleChange={setShowNextPicker}
            overlay={<Picker block={block} mode={mode} node={node} onAdd={handleAddNextChild} />}
            placement='right-start'
            styleType='clear'
            visible={showNextPicker}
          >
            <IconButton
              background='var(--color-flex)'
              icon='add'
              name='addChildNext'
              size={KitSize.XS}
              round
            />
          </DropDown>
        </AbsolutePortal>
      )}
    </>
  )
}

export default React.memo(FlexChildrenControls)
